import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { formatDate } from '../../utils/format';
import { toCsv } from '../../utils/csv.js';

// ─────────────────────────────────────────────────────────────────────────────
// Delivery Analytics — read-only PMO deep-dive into historical delivery
// performance across Active and Completed SOWs. No editing here; the operational
// screens (Delivery Cockpit, Project Update, RAID Register) own the data. This
// screen exists to spot trends and pinch points over time and drill into the
// specifics:
//   • Missed Milestones — on-time vs slipped by month, why (reason / category),
//     and by PM / client, with a drill-down to the exact milestones.
//   • Dependencies — where delivery is blocked and by whom, over time.
// ─────────────────────────────────────────────────────────────────────────────

const DAY = 86400000;
const DONE_STATUSES = ['Ready to bill', 'Ready To Bill', 'Invoiced', 'Paid', 'Complete', 'Completed'];

function parseDate(s) {
  if (!s) return null;
  const t = String(s).slice(0, 10);
  const d = new Date(t + 'T00:00:00Z');
  return isNaN(d.getTime()) ? null : d.getTime();
}
function monthKey(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key) {
  const [y, m] = key.split('-');
  return new Date(Date.UTC(+y, +m - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}
function quarterKey(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
}
function quarterLabel(key) { return key.replace('-', ' '); }

// A milestone "actually landed" on the first of these it has; otherwise we fall
// back to its current forecast to judge whether it is (or is heading) late.
// Deliberately NOT paid_date — payment lags delivery by the payment terms, so
// using it would falsely mark on-time milestones as slipped.
function actualDate(m) {
  return parseDate(m.ready_to_bill_date) || parseDate(m.formal_invoice_date);
}
function isDone(m) { return DONE_STATUSES.includes(m.status); }

// Outcome of a milestone against its committed (contract) date:
//   on_time | missed | pending (committed but not yet comparable) | unassessable (no contract date)
function outcomeOf(m) {
  const base = parseDate(m.contract_date);
  if (!base) return { outcome: 'unassessable', slip: null };
  const cmp = actualDate(m) || parseDate(m.forecast_date);
  const flagged = !!(m.missed_milestone_reason && String(m.missed_milestone_reason).trim());
  if (cmp == null) return { outcome: flagged ? 'missed' : 'pending', slip: null };
  const slip = Math.round((cmp - base) / DAY);
  if (slip > 0 || flagged) return { outcome: 'missed', slip: slip > 0 ? slip : null };
  return { outcome: 'on_time', slip };
}

// Map each missed-milestone reason value (migration 0205 taxonomy) to its
// primary category, so the PMO can see pinch points at category level too.
const REASON_CATEGORY = {
  'Estimation Failure (Effort / Complexity Underestimated)': 'Planning & Definition',
  'Missing / Unidentified Dependency': 'Planning & Definition',
  'Incomplete Scope / Task Definition': 'Planning & Definition',
  'Solution / Design Not Mature at Commitment': 'Planning & Definition',
  'Exit Criteria Not Defined or Not Agreed': 'Planning & Definition',
  'Late Mobilisation / Ownership Not Established': 'Planning & Definition',
  'Milestone Plan (in the SoW) Not Aligned to Delivery Reality': 'Planning & Definition',
  'Resource Not Available When Required': 'Resource & Capacity',
  'Capability Mismatch to Delivery Requirement': 'Resource & Capacity',
  'Insufficient Capacity for Demand': 'Resource & Capacity',
  'Key Person Dependency': 'Resource & Capacity',
  'Approval / Decision Delay': 'Client & External',
  'Environment Not Ready': 'Client & External',
  'Third Party Delivery Failure': 'Client & External',
  'Governance / Assurance Delay': 'Client & External',
  'Dependency Not Delivered to Plan': 'Client & External',
  'Client Sequencing / Priority Change': 'Client & External',
  'Delayed Commercial Approval / PO Release': 'Client & External',
  'Uncontrolled Scope Change': 'Scope & Change Control',
  'Client Introduced Scope Change': 'Scope & Change Control',
  'Design Change Post-Approval': 'Scope & Change Control',
  'Scope Ambiguity in SoW': 'Scope & Change Control',
  'Rework Required Due to Delivery Error': 'Delivery Execution',
  'Poor Task Sequencing / Coordination': 'Delivery Execution',
  'Inadequate Testing Before Milestone': 'Delivery Execution',
  'Cutover / Implementation Failure': 'Delivery Execution',
  'Hardware Lead Time Exceeded': 'Hardware & Procurement',
  'Specification Error': 'Hardware & Procurement',
  'Procurement Delay': 'Hardware & Procurement',
  'Materials Not Available When Required': 'Hardware & Procurement',
  'Client Driven Reprioritisation': 'Priority & Portfolio Conflict',
  'Internal Reprioritisation': 'Priority & Portfolio Conflict',
  'Reactive / Unplanned Work Displacement': 'Priority & Portfolio Conflict',
  'Leadership Override': 'Priority & Portfolio Conflict',
  'Critical Information Not Communicated': 'Communication & Escalation',
  'Late Escalation of Risk / Issue': 'Communication & Escalation',
  'Misalignment Between Teams': 'Communication & Escalation',
};
function reasonCategory(reason) {
  if (!reason) return 'Not categorised';
  const clean = String(reason).trim();
  // Stored labels may carry the "Category — Sub" prefix; take the category half.
  if (clean.includes(' — ')) return clean.split(' — ')[0];
  return REASON_CATEGORY[clean] || 'Other';
}

const DEP_COLORS = { '3rd Party': '#7c3aed', 'Supplier (ATV)': '#0891b2', 'RR': '#d97706' };

function downloadCSV(filename, headers, rows) {
  const csv = toCsv(headers, rows);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Small presentational pieces ────────────────────────────────────────────────
function Stat({ label, value, sub, tone = 'navy' }) {
  const toneCls = { navy: 'text-navy', red: 'text-rose-600', green: 'text-emerald-600', amber: 'text-amber-600', gray: 'text-gray-500' }[tone] || 'text-navy';
  return (
    <div className="bg-white border border-gray-100 rounded p-2.5">
      <div className="text-[8px] font-bold uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-xl font-extrabold leading-tight ${toneCls}`}>{value}</div>
      {sub && <div className="text-[8px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// Stacked monthly volume bars (on-time green / missed red) with an on-time% line.
function MissedTrend({ buckets, onPick, activeKey }) {
  const W = 760, H = 220, PAD = { l: 28, r: 34, t: 12, b: 34 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;
  const maxVol = Math.max(1, ...buckets.map(b => b.onTime + b.missed));
  const n = buckets.length || 1;
  const bw = Math.min(46, (plotW / n) * 0.7);
  const step = plotW / n;
  const yVol = v => PAD.t + plotH - (v / maxVol) * plotH;
  const yPct = p => PAD.t + plotH - (p / 100) * plotH;
  const linePts = buckets
    .map((b, i) => {
      const assessed = b.onTime + b.missed;
      if (!assessed) return null;
      const x = PAD.l + step * i + step / 2;
      return `${x},${yPct((b.onTime / assessed) * 100)}`;
    })
    .filter(Boolean).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 240 }}>
      {[0, 25, 50, 75, 100].map(p => (
        <g key={p}>
          <line x1={PAD.l} x2={W - PAD.r} y1={yPct(p)} y2={yPct(p)} stroke="#f1f1f4" strokeWidth="1" />
          <text x={W - PAD.r + 3} y={yPct(p) + 3} className="fill-gray-300" style={{ fontSize: 7 }}>{p}%</text>
        </g>
      ))}
      {buckets.map((b, i) => {
        const x = PAD.l + step * i + (step - bw) / 2;
        const total = b.onTime + b.missed;
        const hOn = (b.onTime / maxVol) * plotH;
        const hMiss = (b.missed / maxVol) * plotH;
        const active = activeKey === b.key;
        return (
          <g key={b.key} className="cursor-pointer" onClick={() => onPick && onPick(b.key)}>
            <rect x={PAD.l + step * i} y={PAD.t} width={step} height={plotH} fill={active ? '#faf6ee' : 'transparent'} />
            <rect x={x} y={yVol(total)} width={bw} height={hMiss} fill="#e11d48" rx="1" />
            <rect x={x} y={yVol(b.onTime)} width={bw} height={hOn} fill="#10b981" rx="1" />
            {total > 0 && <text x={x + bw / 2} y={yVol(total) - 3} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 7 }}>{total}</text>}
            <text x={x + bw / 2} y={H - PAD.b + 12} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 7 }}>{b.label}</text>
          </g>
        );
      })}
      {linePts && <polyline points={linePts} fill="none" stroke="#1e293b" strokeWidth="1.5" strokeDasharray="2 2" />}
      {buckets.map((b, i) => {
        const assessed = b.onTime + b.missed;
        if (!assessed) return null;
        const x = PAD.l + step * i + step / 2;
        return <circle key={b.key} cx={x} cy={yPct((b.onTime / assessed) * 100)} r="2" fill="#1e293b" />;
      })}
    </svg>
  );
}

// Grouped monthly bars for dependencies, coloured by reason.
function DepTrend({ buckets, reasons }) {
  const W = 760, H = 200, PAD = { l: 28, r: 12, t: 12, b: 34 };
  const plotW = W - PAD.l - PAD.r, plotH = H - PAD.t - PAD.b;
  const maxVol = Math.max(1, ...buckets.map(b => reasons.reduce((s, r) => s + (b.byReason[r] || 0), 0)));
  const n = buckets.length || 1;
  const step = plotW / n;
  const bw = Math.min(46, step * 0.7);
  const yVol = v => PAD.t + plotH - (v / maxVol) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }}>
      {buckets.map((b, i) => {
        let acc = 0;
        const x = PAD.l + step * i + (step - bw) / 2;
        const total = reasons.reduce((s, r) => s + (b.byReason[r] || 0), 0);
        return (
          <g key={b.key}>
            {reasons.map(r => {
              const v = b.byReason[r] || 0;
              if (!v) return null;
              const h = (v / maxVol) * plotH;
              acc += v;
              const y = yVol(acc);
              return <rect key={r} x={x} y={y} width={bw} height={h} fill={DEP_COLORS[r] || '#94a3b8'} rx="1" />;
            })}
            {total > 0 && <text x={x + bw / 2} y={yVol(total) - 3} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 7 }}>{total}</text>}
            <text x={x + bw / 2} y={H - PAD.b + 12} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 7 }}>{b.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Horizontal Pareto list — click a row to drill.
function Pareto({ title, items, total, onPick, activeKey, tone = '#e11d48' }) {
  const max = Math.max(1, ...items.map(i => i.count));
  return (
    <div className="bg-white border border-gray-100 rounded">
      <div className="px-3 py-2 border-b bg-gray-50 text-[10px] font-bold text-navy uppercase">{title}</div>
      <div className="divide-y divide-gray-50">
        {items.length === 0 && <div className="px-3 py-3 text-[9px] text-gray-400 italic">Nothing in range.</div>}
        {items.map(it => {
          const active = activeKey === it.key;
          return (
            <button key={it.key} type="button" onClick={() => onPick && onPick(it.key)}
              className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 ${active ? 'bg-amber-50' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] text-gray-700 truncate" title={it.label}>{it.label}</span>
                <span className="text-[9px] font-bold text-navy flex-shrink-0">
                  {it.count}{total ? <span className="text-gray-400 font-normal"> · {Math.round((it.count / total) * 100)}%</span> : null}
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
                <div className="h-full rounded-full" style={{ width: `${(it.count / max) * 100}%`, background: tone }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function DeliveryAnalyticsPage() {
  const { navigate } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [tab, setTab] = useState('missed');           // missed | dependencies
  const [scope, setScope] = useState('both');          // active | completed | both
  const [range, setRange] = useState('24');            // '12' | '24' | 'all'
  const [gran, setGran] = useState('month');           // month | quarter
  const [client, setClient] = useState('');
  const [pm, setPm] = useState('');
  const [drill, setDrill] = useState(null);            // { dim, key, label }

  useEffect(() => {
    setLoading(true);
    api.get('delivery-analytics')
      .then(d => { setData(d); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const milestones = data?.milestones || [];
  const dependencies = data?.dependencies || [];

  const clients = useMemo(() => [...new Set(milestones.map(m => m.client_name).filter(Boolean))].sort(), [milestones]);
  const pms = useMemo(() => [...new Set(milestones.map(m => m.pm_name).filter(Boolean))].sort(), [milestones]);

  // Range cutoff (by committed/contract month).
  const cutoff = useMemo(() => {
    if (range === 'all') return null;
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() - (Number(range) - 1));
    d.setUTCDate(1);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  }, [range]);

  const scopeMatch = (status) =>
    scope === 'both' ? true : scope === 'active' ? status === 'Active' : status === 'Completed';

  // Milestones enriched with outcome + filtered by the global filters.
  const rows = useMemo(() => milestones
    .map(m => ({ ...m, ...outcomeOf(m) }))
    .filter(m => scopeMatch(m.sow_status))
    .filter(m => !client || m.client_name === client)
    .filter(m => !pm || m.pm_name === pm)
    .filter(m => {
      if (!cutoff) return true;
      const base = parseDate(m.contract_date);
      return base != null && base >= cutoff;
    }), [milestones, scope, client, pm, cutoff]);

  const bucketOf = (ms) => (gran === 'quarter' ? quarterKey(ms) : monthKey(ms));
  const bucketLabel = (key) => (gran === 'quarter' ? quarterLabel(key) : monthLabel(key));

  // ── Missed-milestone aggregates ──────────────────────────────────────────────
  const assessed = rows.filter(m => m.outcome === 'on_time' || m.outcome === 'missed');
  const missedRows = rows.filter(m => m.outcome === 'missed');
  const onTimeCount = assessed.filter(m => m.outcome === 'on_time').length;
  const slips = missedRows.map(m => m.slip).filter(s => s != null && s > 0).sort((a, b) => a - b);
  const avgSlip = slips.length ? Math.round(slips.reduce((a, b) => a + b, 0) / slips.length) : null;
  const medSlip = slips.length ? slips[Math.floor(slips.length / 2)] : null;

  const missedTrend = useMemo(() => {
    const map = new Map();
    for (const m of rows) {
      if (m.outcome !== 'on_time' && m.outcome !== 'missed') continue;
      const base = parseDate(m.contract_date);
      if (base == null) continue;
      const key = bucketOf(base);
      if (!map.has(key)) map.set(key, { key, label: bucketLabel(key), onTime: 0, missed: 0 });
      const b = map.get(key);
      if (m.outcome === 'missed') b.missed++; else b.onTime++;
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [rows, gran]);

  const byReason = useMemo(() => {
    const map = new Map();
    for (const m of missedRows) {
      const key = (m.missed_milestone_reason && String(m.missed_milestone_reason).trim()) || '(no reason recorded)';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].map(([key, count]) => ({ key, label: key, count })).sort((a, b) => b.count - a.count);
  }, [missedRows]);

  const byCategory = useMemo(() => {
    const map = new Map();
    for (const m of missedRows) {
      const key = reasonCategory(m.missed_milestone_reason);
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].map(([key, count]) => ({ key, label: key, count })).sort((a, b) => b.count - a.count);
  }, [missedRows]);

  const byPm = useMemo(() => {
    const map = new Map();
    for (const m of missedRows) {
      const key = m.pm_name || '(unassigned)';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].map(([key, count]) => ({ key, label: key, count })).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [missedRows]);

  const byClient = useMemo(() => {
    const map = new Map();
    for (const m of missedRows) {
      const key = m.client_name || '(no client)';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].map(([key, count]) => ({ key, label: key, count })).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [missedRows]);

  // Drill-down list for the missed tab, honouring any active dimension pick.
  const missedDrill = useMemo(() => {
    let list = missedRows;
    if (drill) {
      if (drill.dim === 'reason') list = list.filter(m => ((m.missed_milestone_reason && String(m.missed_milestone_reason).trim()) || '(no reason recorded)') === drill.key);
      else if (drill.dim === 'category') list = list.filter(m => reasonCategory(m.missed_milestone_reason) === drill.key);
      else if (drill.dim === 'pm') list = list.filter(m => (m.pm_name || '(unassigned)') === drill.key);
      else if (drill.dim === 'client') list = list.filter(m => (m.client_name || '(no client)') === drill.key);
      else if (drill.dim === 'period') list = list.filter(m => { const b = parseDate(m.contract_date); return b != null && bucketOf(b) === drill.key; });
    }
    return list.sort((a, b) => (parseDate(b.contract_date) || 0) - (parseDate(a.contract_date) || 0));
  }, [missedRows, drill, gran]);

  const pickDrill = (dim, key, label) =>
    setDrill(cur => (cur && cur.dim === dim && cur.key === key) ? null : { dim, key, label });

  // ── Dependency aggregates ────────────────────────────────────────────────────
  const depRows = useMemo(() => rows.filter(m => m.dependency_reason && String(m.dependency_reason).trim()), [rows]);
  const depReasons = ['3rd Party', 'Supplier (ATV)', 'RR'];
  const depByReason = depReasons.map(r => ({ reason: r, count: depRows.filter(m => String(m.dependency_reason).trim() === r).length }));
  const depOther = depRows.filter(m => !depReasons.includes(String(m.dependency_reason).trim()));

  const depTrend = useMemo(() => {
    const map = new Map();
    for (const m of depRows) {
      const base = parseDate(m.contract_date) || parseDate(m.forecast_date);
      if (base == null) continue;
      const key = bucketOf(base);
      if (!map.has(key)) map.set(key, { key, label: bucketLabel(key), byReason: {} });
      const r = String(m.dependency_reason).trim();
      const b = map.get(key);
      b.byReason[r] = (b.byReason[r] || 0) + 1;
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [depRows, gran]);

  const raidDeps = useMemo(() => dependencies
    .filter(d => scopeMatch(d.sow_status))
    .filter(d => !client || d.client_name === client)
    .filter(d => {
      if (!cutoff) return true;
      const c = parseDate(d.created_at);
      return c != null && c >= cutoff;
    }), [dependencies, scope, client, cutoff]);
  const raidOpen = raidDeps.filter(d => d.status !== 'closed').length;

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading delivery analytics…</div>;
  if (error) return <div className="p-6 text-rose-500 text-sm">Failed to load delivery analytics.</div>;

  const exportMissed = () => downloadCSV(
    `missed-milestones-${new Date().toISOString().slice(0, 10)}.csv`,
    ['SOW', 'Milestone', 'Description', 'Client', 'PM', 'SOW Status', 'Milestone Status', 'Contract Date', 'Forecast Date', 'Actual Date', 'Slip (days)', 'Reason', 'Category'],
    missedDrill.map(m => [
      m.sow_reference, m.milestone_code, m.description, m.client_name, m.pm_name, m.sow_status, m.status,
      m.contract_date || '', m.forecast_date || '', (m.ready_to_bill_date || m.formal_invoice_date || ''),
      m.slip != null ? m.slip : '', m.missed_milestone_reason || '', reasonCategory(m.missed_milestone_reason),
    ]),
  );
  const exportDeps = () => downloadCSV(
    `milestone-dependencies-${new Date().toISOString().slice(0, 10)}.csv`,
    ['SOW', 'Milestone', 'Description', 'Client', 'PM', 'SOW Status', 'Milestone Status', 'Contract Date', 'Forecast Date', 'Dependency Reason', 'Detail'],
    depRows.map(m => [
      m.sow_reference, m.milestone_code, m.description, m.client_name, m.pm_name, m.sow_status, m.status,
      m.contract_date || '', m.forecast_date || '', m.dependency_reason || '', m.dependency_comment || m.dependency_delay || '',
    ]),
  );

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2 text-[9px]">
      <div className="flex rounded overflow-hidden border border-gray-200">
        {[['active', 'Active'], ['completed', 'Completed'], ['both', 'Both']].map(([v, l]) => (
          <button key={v} onClick={() => setScope(v)} className={`px-2 py-1 font-bold ${scope === v ? 'bg-navy text-white' : 'bg-white text-gray-500'}`}>{l}</button>
        ))}
      </div>
      <div className="flex rounded overflow-hidden border border-gray-200">
        {[['12', '12m'], ['24', '24m'], ['all', 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setRange(v)} className={`px-2 py-1 font-bold ${range === v ? 'bg-navy text-white' : 'bg-white text-gray-500'}`}>{l}</button>
        ))}
      </div>
      <div className="flex rounded overflow-hidden border border-gray-200">
        {[['month', 'Monthly'], ['quarter', 'Quarterly']].map(([v, l]) => (
          <button key={v} onClick={() => setGran(v)} className={`px-2 py-1 font-bold ${gran === v ? 'bg-navy text-white' : 'bg-white text-gray-500'}`}>{l}</button>
        ))}
      </div>
      <select value={client} onChange={e => setClient(e.target.value)} className="form-input text-[9px] py-1 max-w-[180px]">
        <option value="">All clients</option>
        {clients.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={pm} onChange={e => setPm(e.target.value)} className="form-input text-[9px] py-1 max-w-[160px]">
        <option value="">All PMs</option>
        {pms.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  );

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Delivery Analytics</h2>
          <p className="text-[9px] text-gray-400">Historical delivery performance across Active &amp; Completed SOWs — read-only. Committed milestone dates drive the trend.</p>
        </div>
        <div className="flex rounded overflow-hidden border border-gray-200 text-[10px]">
          {[['missed', 'Missed Milestones'], ['dependencies', 'Dependencies']].map(([v, l]) => (
            <button key={v} onClick={() => { setTab(v); setDrill(null); }} className={`px-3 py-1.5 font-bold ${tab === v ? 'bg-gold text-white' : 'bg-white text-gray-500'}`}>{l}</button>
          ))}
        </div>
      </div>

      {filterBar}

      {tab === 'missed' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Committed (assessed)" value={assessed.length} sub={`${rows.filter(m => m.outcome === 'pending').length} pending · ${rows.filter(m => m.outcome === 'unassessable').length} no contract date`} tone="navy" />
            <Stat label="On-time" value={onTimeCount} tone="green" />
            <Stat label="Missed / slipped" value={missedRows.length} tone="red" />
            <Stat label="On-time rate" value={assessed.length ? `${Math.round((onTimeCount / assessed.length) * 100)}%` : '—'} tone={assessed.length && onTimeCount / assessed.length >= 0.8 ? 'green' : 'amber'} />
            <Stat label="Avg / median slip" value={avgSlip != null ? `${avgSlip}d` : '—'} sub={medSlip != null ? `median ${medSlip}d` : ''} tone="amber" />
          </div>

          <div className="bg-white border border-gray-100 rounded">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
              <span className="text-[10px] font-bold text-navy uppercase">On-time vs missed over time</span>
              <span className="text-[8px] text-gray-400">
                <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#10b981' }} />on-time
                <span className="inline-block w-2 h-2 rounded-sm mx-1 ml-3" style={{ background: '#e11d48' }} />missed
                <span className="ml-3">┄ on-time %</span>
              </span>
            </div>
            <div className="p-2">
              {missedTrend.length ? <MissedTrend buckets={missedTrend} activeKey={drill?.dim === 'period' ? drill.key : null} onPick={k => pickDrill('period', k, bucketLabel(k))} />
                : <div className="text-[9px] text-gray-400 italic p-4 text-center">No committed milestones in range.</div>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <Pareto title="Pinch points — by category" items={byCategory} total={missedRows.length} activeKey={drill?.dim === 'category' ? drill.key : null} onPick={k => pickDrill('category', k, k)} tone="#e11d48" />
            <Pareto title="Pinch points — by reason" items={byReason} total={missedRows.length} activeKey={drill?.dim === 'reason' ? drill.key : null} onPick={k => pickDrill('reason', k, k)} tone="#f43f5e" />
            <Pareto title="Missed by PM" items={byPm} total={missedRows.length} activeKey={drill?.dim === 'pm' ? drill.key : null} onPick={k => pickDrill('pm', k, k)} tone="#0891b2" />
            <Pareto title="Missed by client" items={byClient} total={missedRows.length} activeKey={drill?.dim === 'client' ? drill.key : null} onPick={k => pickDrill('client', k, k)} tone="#7c3aed" />
          </div>

          <div className="bg-white border border-gray-100 rounded">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[10px] font-bold text-navy uppercase">
                Drill-down — {missedDrill.length} missed milestone{missedDrill.length === 1 ? '' : 's'}
                {drill && <button onClick={() => setDrill(null)} className="ml-2 text-[8px] font-bold text-rose-500 normal-case">✕ {drill.dim}: {drill.label}</button>}
              </span>
              <button onClick={exportMissed} className="px-2 py-1 text-[8px] font-bold bg-navy text-white rounded">Export CSV</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead className="bg-gray-50 text-gray-400 uppercase">
                  <tr>
                    {['SOW', 'Milestone', 'Client', 'PM', 'Status', 'Contract', 'Forecast/Actual', 'Slip', 'Reason'].map(h => <th key={h} className="px-2 py-1.5 text-left font-bold whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {missedDrill.length === 0 && <tr><td colSpan={9} className="px-2 py-4 text-center text-gray-400 italic">No missed milestones for this selection.</td></tr>}
                  {missedDrill.slice(0, 400).map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate('sow-detail', m.sow_id)} title="Open SOW">
                      <td className="px-2 py-1.5 font-bold text-navy whitespace-nowrap">{m.sow_reference}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.milestone_code}<span className="text-gray-400 ml-1" title={m.description}>{(m.description || '').slice(0, 28)}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.client_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.pm_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.status}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(m.contract_date)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(m.ready_to_bill_date || m.formal_invoice_date || m.forecast_date)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap font-bold text-rose-600">{m.slip != null ? `+${m.slip}d` : '—'}</td>
                      <td className="px-2 py-1.5" title={m.missed_milestone_reason || ''}><span className="text-gray-600">{(m.missed_milestone_reason || '—')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {missedDrill.length > 400 && <div className="px-2 py-2 text-[8px] text-gray-400 italic">Showing first 400 of {missedDrill.length}. Narrow the filters or export the full set.</div>}
            </div>
          </div>
        </>
      )}

      {tab === 'dependencies' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Milestone dependencies" value={depRows.length} sub="milestones flagged blocked" tone="navy" />
            {depByReason.map(d => <Stat key={d.reason} label={d.reason} value={d.count} tone="amber" />)}
            <Stat label="RAID dependencies" value={raidDeps.length} sub={`${raidOpen} open`} tone="red" />
          </div>

          <div className="bg-white border border-gray-100 rounded">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
              <span className="text-[10px] font-bold text-navy uppercase">Milestone dependencies over time</span>
              <span className="text-[8px] text-gray-400">
                {depReasons.map(r => <span key={r} className="ml-3"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: DEP_COLORS[r] }} />{r}</span>)}
              </span>
            </div>
            <div className="p-2">
              {depTrend.length ? <DepTrend buckets={depTrend} reasons={depReasons} />
                : <div className="text-[9px] text-gray-400 italic p-4 text-center">No milestone dependencies in range.</div>}
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[10px] font-bold text-navy uppercase">Milestone dependencies — {depRows.length}</span>
              <button onClick={exportDeps} className="px-2 py-1 text-[8px] font-bold bg-navy text-white rounded">Export CSV</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead className="bg-gray-50 text-gray-400 uppercase">
                  <tr>{['SOW', 'Milestone', 'Client', 'PM', 'Status', 'Contract', 'Reason', 'Detail'].map(h => <th key={h} className="px-2 py-1.5 text-left font-bold whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {depRows.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-gray-400 italic">No milestone dependencies for this selection.</td></tr>}
                  {depRows.slice(0, 400).map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate('sow-detail', m.sow_id)} title="Open SOW">
                      <td className="px-2 py-1.5 font-bold text-navy whitespace-nowrap">{m.sow_reference}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.milestone_code}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.client_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.pm_name || '—'}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{m.status}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(m.contract_date)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap"><span className="px-1.5 py-0.5 rounded text-white text-[8px] font-bold" style={{ background: DEP_COLORS[String(m.dependency_reason).trim()] || '#94a3b8' }}>{m.dependency_reason}</span></td>
                      <td className="px-2 py-1.5" title={m.dependency_comment || m.dependency_delay || ''}>{(m.dependency_comment || m.dependency_delay || '—').slice(0, 60)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {depOther.length > 0 && <div className="px-2 py-2 text-[8px] text-gray-400 italic">{depOther.length} dependency milestone(s) use a reason outside the standard 3-way taxonomy — review in Admin › Settings › Dependency Reason.</div>}
            </div>
          </div>

          {raidDeps.length > 0 && (
            <div className="bg-white border border-gray-100 rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-[10px] font-bold text-navy uppercase">RAID log dependencies — {raidDeps.length} ({raidOpen} open)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[9px]">
                  <thead className="bg-gray-50 text-gray-400 uppercase">
                    <tr>{['Ref', 'Title', 'SOW', 'Client', 'Status', 'Raised'].map(h => <th key={h} className="px-2 py-1.5 text-left font-bold whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {raidDeps.slice(0, 200).map(d => (
                      <tr key={d.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => d.sow_id && navigate('sow-detail', d.sow_id)} title="Open SOW">
                        <td className="px-2 py-1.5 font-bold text-navy whitespace-nowrap">{d.reference}</td>
                        <td className="px-2 py-1.5" title={d.description || ''}>{(d.title || '').slice(0, 60)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{d.sow_reference || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{d.client_name || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${d.status === 'closed' ? 'bg-gray-100 text-gray-500' : 'bg-rose-100 text-rose-700'}`}>{d.status}</span></td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDate(d.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
